import { Skeleton } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import {
  RobotoStyledPopoverMenu as Menu,
  RobotoMenuItem as MenuItem,
  RobotoLinkButton as Link,
  RobotoLink,
} from "@/shared/components";
import { FileRecord } from "@/shared/domain/files";
import { basename, dirname } from "@/shared/domain/files/filesystem";
import { usePopupState } from "@/shared/hooks";
import { Goto } from "@/shared/navigation";

import CopyToClipboardButton from "../../../CopyToClipboardButton";

import styles from "./FileInfo.module.css";

/**
 * Displays file information - either a single filename or count of multiple files.
 */
export function FileInfo({
  isFilesLoading,
  files,
}: {
  isFilesLoading: boolean;
  files: FileRecord[];
}) {
  const { goto } = useNavigation();
  const popup = usePopupState();

  const [isHovering, setIsHovering] = React.useState(false);

  const fileNames = files
    .map((file) => basename(file?.relative_path ?? ""))
    .join(", ");

  if (isFilesLoading) {
    return <Skeleton animation="wave" variant="text" width="160px" />;
  }

  if (files.length === 1) {
    return (
      <div
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={styles.fileInfoContainer}
      >
        <RobotoLink
          underline="hover"
          color="inherit"
          className={styles.fileInfo}
          sx={{ marginRight: "0.5rem" }}
          to={Goto.createDatasetRoute(
            files[0].association_id,
            dirname(files[0].relative_path),
          )}
          title={fileNames}
        >
          {basename(files[0].relative_path)}
        </RobotoLink>
        <CopyToClipboardButton
          text={fileNames}
          sx={{
            opacity: isHovering ? 1 : 0,
            transition: "opacity 0.2s",
            flexShrink: 0,
          }}
        />
      </div>
    );
  }
  return (
    <>
      <Menu anchorEl={popup.anchor} open={popup.isOpen} onClose={popup.close}>
        {files.map((file) => {
          return (
            <MenuItem
              key={file.file_id}
              eventName={"ViewDatasetForFileClicked"}
              eventProperties={{
                datasetId: file.association_id,
                fileId: file.file_id,
              }}
              onClick={() =>
                goto.dataset({
                  datasetId: file.association_id,
                  directoryPath: dirname(file.relative_path),
                })
              }
            >
              {basename(file.relative_path)}
            </MenuItem>
          );
        })}
      </Menu>

      <div>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          className={styles.fileInfo}
          onClick={(event) => popup.open({ anchorEl: event.currentTarget })}
          title={fileNames}
        >
          {files.length} Files
        </Link>
      </div>
    </>
  );
}
